// MassDeleteConfirm — 2-step deletion confirmation modal.
// Step 1: warning + summary, [Abbrechen] [Weiter, ich bin mir sicher]
// Step 2: typed phrase ("LOESCHEN") + [Endgültig löschen]
import { useEffect, useState } from "react";
import { AlertTriangle, ShieldAlert, X, Loader2 } from "lucide-react";

export default function MassDeleteConfirm({
  open,
  count,
  label,
  confirmPhrase = "LOESCHEN",
  onCancel,
  onConfirm,            // async () => void
}) {
  const [step, setStep] = useState(1);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setStep(1); setTyped(""); setBusy(false); }
  }, [open]);

  if (!open) return null;

  async function doConfirm() {
    setBusy(true);
    try { await onConfirm(); }
    finally { setBusy(false); }
  }

  const canSubmit = typed.trim().toUpperCase() === confirmPhrase.toUpperCase();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="mass-delete-confirm">
      <div className="bg-zinc-900 border border-rose-500/30 rounded-lg shadow-2xl w-full max-w-md overflow-hidden">
        <header className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2 bg-rose-500/5">
          <ShieldAlert className="w-5 h-5 text-rose-400" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-rose-300 font-semibold">
              Endgültige Löschung · Schritt {step} von 2
            </div>
            <h2 className="font-display font-bold text-zinc-50 text-base mt-0.5">
              {label || "Einträge löschen"}
            </h2>
          </div>
          <button onClick={onCancel} data-testid="mass-delete-cancel-x"
            className="p-1 text-zinc-500 hover:text-zinc-200 rounded">
            <X className="w-4 h-4" />
          </button>
        </header>

        {step === 1 && (
          <>
            <div className="p-5 space-y-3">
              <div className="flex items-start gap-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded">
                <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                <div className="text-sm text-zinc-200">
                  Du bist im Begriff{" "}
                  <span className="font-bold text-amber-200">{count}</span> Einträge endgültig zu löschen.
                  <div className="text-xs text-zinc-400 mt-1">
                    Diese Aktion kann <span className="font-semibold text-rose-300">nicht rückgängig gemacht werden</span>.
                  </div>
                </div>
              </div>
              <p className="text-xs text-zinc-500">
                Klicke „Weiter", um zur finalen Bestätigung zu gelangen.
              </p>
            </div>
            <footer className="px-5 py-3 bg-zinc-950/60 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={onCancel} data-testid="mass-delete-step1-cancel"
                className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200">
                Abbrechen
              </button>
              <button onClick={() => setStep(2)} data-testid="mass-delete-step1-continue"
                className="px-4 py-2 text-sm bg-rose-600 hover:bg-rose-700 text-white rounded font-medium">
                Weiter →
              </button>
            </footer>
          </>
        )}

        {step === 2 && (
          <>
            <div className="p-5 space-y-3">
              <div className="p-3 bg-rose-500/5 border border-rose-500/30 rounded text-sm">
                <div className="text-rose-200 font-semibold mb-1">Letzte Bestätigung</div>
                <div className="text-zinc-300 text-xs">
                  Tippe <span className="font-mono bg-zinc-950 px-1.5 py-0.5 rounded border border-zinc-700 text-rose-200">{confirmPhrase}</span> in das Feld unten, um {count} Einträge unwiderruflich zu löschen.
                </div>
              </div>
              <input
                type="text"
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={confirmPhrase}
                data-testid="mass-delete-typed-input"
                className="w-full px-3 py-2.5 bg-zinc-950 border border-zinc-700 rounded text-sm text-zinc-100 font-mono tracking-wider focus:border-rose-500/60 outline-none"
              />
            </div>
            <footer className="px-5 py-3 bg-zinc-950/60 border-t border-zinc-800 flex justify-between items-center">
              <button onClick={() => setStep(1)} disabled={busy}
                data-testid="mass-delete-step2-back"
                className="text-xs text-zinc-500 hover:text-zinc-300">
                ← Zurück
              </button>
              <div className="flex gap-2">
                <button onClick={onCancel} disabled={busy}
                  data-testid="mass-delete-step2-cancel"
                  className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200">
                  Abbrechen
                </button>
                <button onClick={doConfirm}
                  disabled={!canSubmit || busy}
                  data-testid="mass-delete-confirm-final"
                  className="px-4 py-2 text-sm bg-rose-600 hover:bg-rose-700 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded font-medium inline-flex items-center gap-2">
                  {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Endgültig löschen
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
